import Close from '@mui/icons-material/Close';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { DialogProps } from '@mui/material/Dialog/Dialog';
import classNames from 'classnames';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

type UiKitModalProps = {
  open: boolean;
  onClose: () => void;
  /** Rendered inside DialogTitle — pass a string or any ReactNode */
  title?: ReactNode;
  /** Fully custom header slot — if provided, replaces the default title rendering */
  header?: ReactNode;
  /** Footer slot — pass buttons or any ReactNode */
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  showCloseButton?: boolean;
  maxWidth?: DialogProps['maxWidth'];
  maxContentHeight?: number;
  hideBackdrop?: boolean;
  /** Inline (horizontal) padding for the content area in px. Defaults to 0. */
  contentPaddingInline?: number;
};

const Modal = ({
  open,
  onClose,
  title,
  header,
  actions,
  children,
  className,
  showCloseButton = true,
  maxWidth = false,
  maxContentHeight,
  hideBackdrop = false,
  contentPaddingInline,
}: UiKitModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [shadowTop, setShadowTop] = useState(false);
  const [shadowBottom, setShadowBottom] = useState(false);

  const checkScroll = useCallback(() => {
    const el = contentRef.current;

    if (!el) {
      return;
    }

    setShadowTop(el.scrollTop > 0);
    setShadowBottom(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  useEffect(() => {
    if (!maxContentHeight) {
      return;
    }

    // Re-check when content changes or modal opens
    const frame = requestAnimationFrame(checkScroll);

    return () => cancelAnimationFrame(frame);
  }, [maxContentHeight, children, open, checkScroll]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      hideBackdrop={hideBackdrop}
      className={classNames('ui-kit-modal', className)}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: '#0B0B0BB2',
            opacity: 0.7,
          },
        },
      }}
    >
      {showCloseButton && (
        <IconButton className="ui-kit-modal-close-btn" onClick={onClose} aria-label="close">
          <Close />
        </IconButton>
      )}

      <div className="ui-kit-modal-body">
        {(header !== undefined || title !== undefined) && (
          <DialogTitle
            className={classNames('ui-kit-modal-header', {
              'ui-kit-modal-header--shadow': shadowTop,
              'ui-kit-modal-header--with-close': showCloseButton,
            })}
          >
            {header ?? title}
          </DialogTitle>
        )}

        <DialogContent
          ref={contentRef}
          className="ui-kit-modal-content"
          style={{
            maxHeight: maxContentHeight ? `${maxContentHeight}px` : undefined,
            paddingInline: contentPaddingInline !== undefined ? `${contentPaddingInline}px` : undefined,
          }}
          onScroll={maxContentHeight ? checkScroll : undefined}
        >
          {children}
        </DialogContent>
      </div>

      {actions !== undefined && (
        <DialogActions className={classNames('ui-kit-modal-actions', { 'ui-kit-modal-actions--shadow': shadowBottom })}>
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default Modal;
